// Model Solution panel (docs/05-raster-grading.md §2,§8).
//
// Top: a project-level **full model-solution document** (the teacher's complete
// "Lösungen" PDF) with Upload/Delete, plus a title-row **Run OCR** (for a
// scanned solution) and **Assign** toggle. In Assign mode the centre shows the
// full solution; Kim highlights a passage and clicks a task card to assign that
// passage as the task's model solution (stored via the per-task
// `model_solution_text` endpoint, then fed into evaluation).
//
// Below: one card per confirmed Exam task with the existing per-task actions
// (Manual / Upload / Generate / Delete) — unchanged.

import { useRef, useState } from "react";
import { useSWRConfig } from "swr";
import { useTranslation } from "react-i18next";

import { ApiError, apiFetch, apiJson } from "../../api/client";
import { useExamTasks } from "../../api/hooks";
import type { ExamTask, ExamTasksResponse, Project } from "../../api/types";
import { useModelUiStore, portionsText } from "../../state/modelUiStore";
import { toast } from "../../lib/toast";
import { useOcrWatch } from "../../lib/useOcrWatch";
import { DeleteSolutionModal } from "./DeleteSolutionModal";

import "../CreateProjectModal.css";
import "../ModelAssignPreview.css";

const PROJECTS_KEY = "/api/projects/?archived=0";

export function ModelSolutionPanel({ project }: { project: Project }) {
  const { t } = useTranslation();
  const { mutate } = useSWRConfig();
  const { data, mutate: mutateTasks } = useExamTasks(project.id);

  const tasks = (data?.tasks ?? []).filter((tk) => tk.confirmed);

  const assignMode = useModelUiStore((s) => s.assignMode);
  const setAssignMode = useModelUiStore((s) => s.setAssignMode);
  const portions = useModelUiStore((s) => s.portions);
  const clearPortions = useModelUiStore((s) => s.clearPortions);
  const pendingText = portionsText(portions);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingTextArea, setPendingTextArea] = useState("");
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState<null | "upload" | "delete">(null);
  const [ocrRunning, setOcrRunning] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [confirmDeleteFull, setConfirmDeleteFull] = useState(false);
  const [assignTask, setAssignTask] = useState<ExamTask | null>(null);

  const hasFull = !!project.model_full_pdf_path;
  const isScan = hasFull && !(project.model_full_text ?? "").trim();
  const hasDigitized = !!project.model_full_digitized_pdf_path;
  const hasContent = !!(pendingFile || pendingTextArea.trim());

  const statusLine =
    status ??
    (assignMode && isScan && !hasDigitized
      ? t("model_solution_panel.full_no_digital_text")
      : assignMode
        ? pendingText
          ? t("model_solution_panel.assign_ready")
          : t("model_solution_panel.assign_hint")
        : null);

  async function refresh(fresh: ExamTasksResponse) {
    await mutateTasks(fresh, { revalidate: false });
    await mutate((key: unknown) => typeof key === "string" && key.startsWith("/api/projects/"));
  }

  async function handleUploadFull(file?: File) {
    setBusy("upload");
    try {
      const fd = new FormData();
      const f = file ?? pendingFile;
      if (f) fd.append("file", f);
      else fd.append("file", new File([pendingTextArea.trim()], "solution.txt", { type: "text/plain" }));
      const res = await apiFetch(`/api/projects/${project.id}/set_model_full`, { method: "POST", body: fd });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await mutate(PROJECTS_KEY);
      toast(t("model_solution_panel.full_uploaded"));
      setPendingFile(null);
      setPendingTextArea("");
      setStatus(null);
    } catch {
      toast(t("model_solution_panel.full_upload_failed"));
    } finally {
      setBusy(null);
    }
  }

  function handleUploadOrPick() {
    if (hasContent) void handleUploadFull();
    else fileInputRef.current?.click();
  }

  async function handleDeleteFull() {
    setBusy("delete");
    try {
      await apiJson(`/api/projects/${project.id}/model_full`, { method: "DELETE" });
      await mutate(PROJECTS_KEY);
      setAssignMode(false);
      setStatus(null);
      toast(t("model_solution_panel.full_deleted"));
      setConfirmDeleteFull(false);
    } catch {
      toast(t("model_solution_panel.full_delete_failed"));
    } finally {
      setBusy(null);
    }
  }

  // Reconnects the status line to a running model-solution OCR job on (re)mount,
  // and watches the job started below — so leaving the view and coming back
  // resumes the status instead of losing it (the job runs server-side).
  const { startWatch } = useOcrWatch({
    projectId: project.id,
    kind: "model_full",
    setStatus,
    setRunning: setOcrRunning,
    labels: {
      progress: (page, total) => t("model_solution_panel.full_ocr_progress", { page, total }),
      done: (count) => t("model_solution_panel.full_ocr_done", { count }),
      failed: t("model_solution_panel.full_ocr_failed"),
    },
    onDone: () => mutate(PROJECTS_KEY),
  });

  async function onRunOcr() {
    setOcrRunning(true);
    setStatus(t("model_solution_panel.full_sending_to_azure"));
    try {
      // OCR runs in the background (202); the watch polls /ocr_status until it
      // finishes, so a multi-page solution doesn't hold the request open past the
      // proxy timeout.
      await apiJson(`/api/projects/${project.id}/run_ocr_model_full`, { method: "POST" });
      startWatch();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) setStatus(t("model_solution_panel.full_ocr_not_needed"));
      else setStatus(t("model_solution_panel.full_ocr_failed"));
      setOcrRunning(false);
    }
  }

  async function assignToTask(task: ExamTask) {
    try {
      const fresh = await apiJson<ExamTasksResponse>(
        `/api/projects/${project.id}/tasks/${task.id}/model_solution_text`,
        { method: "POST", body: JSON.stringify({ text: pendingText }) },
      );
      await refresh(fresh);
      clearPortions();
      setAssignTask(null);
      toast(t("model_solution_panel.assigned", { task: task.label || task.operator || "" }));
    } catch {
      toast(t("model_solution_panel.assign_failed"));
    }
  }

  function onCardClick(task: ExamTask) {
    if (assignMode && pendingText) setAssignTask(task);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    const file = [...e.dataTransfer.files].find((f) => {
      const n = f.name.toLowerCase();
      return n.endsWith(".pdf") || n.endsWith(".txt") || n.endsWith(".md");
    });
    if (file) {
      setPendingFile(file);
      setPendingTextArea("");
    }
  }

  return (
    <div className="details-content">
      <div className="details-title-row">
        <div className="details-title">{t("paper_review.model_solution")}</div>
        <div className="exam-actions">
          <button
            type="button"
            className="model-task__generate exam-ocr-btn"
            onClick={() => void onRunOcr()}
            disabled={!hasFull || ocrRunning}
            title={t("model_solution_panel.full_run_ocr_hint")}
          >
            {ocrRunning ? t("model_solution_panel.full_running_ocr") : t("model_solution_panel.full_run_ocr")}
          </button>
          <button
            type="button"
            className={"model-task__generate exam-analyze-btn" + (assignMode ? " exam-analyze-btn--on" : "")}
            onClick={() => setAssignMode(!assignMode)}
            disabled={!hasFull}
            title={t("model_solution_panel.assign_btn_hint")}
          >
            {assignMode ? t("model_solution_panel.assign_exit") : t("model_solution_panel.assign")}
          </button>
        </div>
      </div>
      <div className="details-subtitle">{project.name}</div>

      <p className="details-description">{t("model_solution_panel.full_description")}</p>

      {statusLine && (
        <div className={"exam-status" + (ocrRunning ? " exam-status--busy" : "")} role="status">
          {ocrRunning && <span className="model-task__spinner" aria-hidden />}
          {statusLine}
        </div>
      )}

      {/* The drag/drop + paste box only appears before a full solution exists;
          once one is uploaded it's hidden so the task list has more room — a
          substitute is uploaded straight from the file picker (Upload below). */}
      {!hasFull && (
        <div
          className={
            "model-upload-zone exam-upload-zone" +
            (dragging ? " model-upload-zone--over" : "") +
            (pendingFile ? " model-upload-zone--has-file" : "")
          }
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragging(false); }}
          onDrop={handleDrop}
        >
          {pendingFile ? (
            <div className="model-upload-file-row">
              <span className="model-upload-file-name">{pendingFile.name}</span>
              <button type="button" className="model-upload-remove" onClick={() => setPendingFile(null)}>×</button>
            </div>
          ) : (
            <>
              <textarea
                className="model-upload-textarea exam-upload-textarea"
                placeholder={t("model_solution_panel.full_paste_placeholder")}
                value={pendingTextArea}
                onChange={(e) => setPendingTextArea(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
              <div className="model-upload-hint">{t("model_solution_panel.full_drag_hint")}</div>
            </>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.txt,.md,application/pdf,text/plain,text/markdown"
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            // With a solution present the drop zone is hidden, so a picked
            // substitute uploads immediately; otherwise stage it for preview.
            if (hasFull) {
              void handleUploadFull(file);
            } else {
              setPendingFile(file);
              setPendingTextArea("");
            }
          }
          e.target.value = "";
        }}
      />

      {hasFull ? (
        <div className="model-upload-footer model-upload-footer--split">
          <button type="button" className="model-upload-submit-btn" disabled={busy != null} onClick={handleUploadOrPick}>
            {busy === "upload" ? t("model_solution_panel.full_uploading") : t("model_solution_panel.full_upload_btn")}
          </button>
          <button type="button" className="model-upload-delete-btn exam-upload-delete-btn" disabled={busy != null} onClick={() => setConfirmDeleteFull(true)}>
            {t("common.delete")}
          </button>
        </div>
      ) : (
        <div className="model-upload-footer">
          <button type="button" className="model-upload-submit-btn" disabled={busy != null} onClick={handleUploadOrPick}>
            {busy === "upload" ? t("model_solution_panel.full_uploading") : t("model_solution_panel.full_upload_solution_btn")}
          </button>
        </div>
      )}

      <div className="exam-tasks">
        <div className="exam-tasks__heading">{t("model_solution_panel.tasks_heading")}</div>
      </div>
      {tasks.length === 0 ? (
        <div className="exam-tasks__empty">{t("model_solution_panel.no_tasks")}</div>
      ) : (
        tasks.map((task) => (
          <TaskSolutionCard
            key={task.id}
            project={project}
            task={task}
            onChanged={refresh}
            assignMode={assignMode}
            canAssign={assignMode && !!pendingText}
            onAssignClick={() => onCardClick(task)}
          />
        ))
      )}

      {assignTask && (
        <AssignModal
          taskTitle={assignTask.label || assignTask.operator || ""}
          text={pendingText}
          onConfirm={() => void assignToTask(assignTask)}
          onClose={() => setAssignTask(null)}
        />
      )}

      {confirmDeleteFull && (
        <ConfirmModal
          title={t("model_solution_panel.full_delete_title")}
          body={t("model_solution_panel.full_delete_confirm")}
          confirmLabel={busy === "delete" ? t("model_solution_panel.full_deleting") : t("common.delete")}
          danger
          busy={busy === "delete"}
          onConfirm={() => void handleDeleteFull()}
          onClose={() => setConfirmDeleteFull(false)}
        />
      )}
    </div>
  );
}

function TaskSolutionCard({
  project,
  task,
  onChanged,
  assignMode,
  canAssign,
  onAssignClick,
}: {
  project: Project;
  task: ExamTask;
  onChanged: (fresh: ExamTasksResponse) => Promise<void>;
  assignMode: boolean;
  canAssign: boolean;
  onAssignClick: () => void;
}) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<null | "upload" | "generate" | "delete">(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const highlighted = useModelUiStore((s) => s.highlightTaskId === task.id);
  const editing = useModelUiStore((s) => s.editingTaskId === task.id);
  const toggleHighlight = useModelUiStore((s) => s.toggleHighlight);
  const setEditing = useModelUiStore((s) => s.setEditing);

  const hasSolution = !!(task.model_solution_path || task.model_solution_text);
  const title = task.label || task.operator || task.criterion_key;
  const words = task.model_solution_words ?? 0;

  async function handleUpload(file: File) {
    setBusy("upload");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await apiFetch(`/api/projects/${project.id}/tasks/${task.id}/model_solution`, { method: "POST", body: fd });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await onChanged((await res.json()) as ExamTasksResponse);
      toast(t("model_solution_panel.uploaded"));
    } catch {
      toast(t("model_solution_panel.upload_failed"));
    } finally {
      setBusy(null);
    }
  }

  async function handleGenerate() {
    setBusy("generate");
    try {
      const fresh = await apiJson<ExamTasksResponse>(`/api/projects/${project.id}/tasks/${task.id}/generate_solution`, { method: "POST" });
      await onChanged(fresh);
      toast(t("model_solution_panel.generated"));
    } catch (err) {
      toast((err as Error).message || t("model_solution_panel.generate_failed"));
    } finally {
      setBusy(null);
    }
  }

  async function handleDelete() {
    setBusy("delete");
    try {
      const fresh = await apiJson<ExamTasksResponse>(`/api/projects/${project.id}/tasks/${task.id}/model_solution`, { method: "DELETE" });
      if (editing) setEditing(null);
      await onChanged(fresh);
      toast(t("model_solution_panel.deleted"));
      setConfirmDelete(false);
    } catch {
      toast(t("model_solution_panel.delete_failed"));
    } finally {
      setBusy(null);
    }
  }

  function handleManual() {
    setEditing(editing ? null : task.id);
  }

  // In Assign mode with a pending highlight, clicking the card assigns that
  // passage; otherwise it toggles the centre highlight as before.
  function onClick() {
    if (assignMode) onAssignClick();
    else toggleHighlight(task.id);
  }

  return (
    <div
      className={
        "model-task model-task--card" +
        (highlighted ? " model-task--active" : "") +
        (canAssign ? " model-task--assignable" : "")
      }
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className="model-task__header">
        <span className="model-task__title">
          {title}
          {hasSolution && <span className="model-task__badge"> ✓</span>}
        </span>
        <span className="model-task__count">{t("paper_review.word_count", { count: words })}</span>
      </div>

      {(task.statement || task.anchor) && (
        <div className="model-task__statement">{task.statement || task.anchor}</div>
      )}

      {busy === "generate" && (
        <div className="model-task__status">
          <span className="model-task__spinner" aria-hidden />
          {t("model_solution_panel.generating_status")}
        </div>
      )}

      <div className="model-task__actions" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="model-task__btn model-task__btn--accent" disabled={busy != null} onClick={() => void handleGenerate()}>
          {t("model_solution_panel.generate_btn")}
        </button>
        <button type="button" className="model-task__btn" disabled={busy != null} onClick={() => fileInputRef.current?.click()}>
          {busy === "upload" ? t("model_solution_panel.uploading") : t("model_solution_panel.upload_btn")}
        </button>
        <button type="button" className={"model-task__btn" + (editing ? " model-task__btn--on" : "")} disabled={busy != null} onClick={handleManual}>
          {t("model_solution_panel.manual_btn")}
        </button>
        <button type="button" className="model-task__btn model-task__btn--danger" disabled={busy != null || !hasSolution} onClick={() => setConfirmDelete(true)}>
          {t("common.delete")}
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.txt,.md,application/pdf,text/plain,text/markdown"
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleUpload(file);
          e.target.value = "";
        }}
      />

      {confirmDelete && (
        <div onClick={(e) => e.stopPropagation()}>
          <DeleteSolutionModal
            taskTitle={title}
            busy={busy === "delete"}
            onConfirm={() => void handleDelete()}
            onClose={() => setConfirmDelete(false)}
          />
        </div>
      )}
    </div>
  );
}

// Centre confirm modal: assign the highlighted passage to a task (Ophelia style).
function AssignModal({
  taskTitle,
  text,
  onConfirm,
  onClose,
}: {
  taskTitle: string;
  text: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <ConfirmModal
      title={t("model_solution_panel.assign_confirm_title")}
      body={t("model_solution_panel.assign_confirm", { task: taskTitle })}
      preview={text}
      confirmLabel={t("model_solution_panel.assign_confirm_cta")}
      busy={false}
      onConfirm={onConfirm}
      onClose={onClose}
    />
  );
}

// Small shared Ophelia-style confirm modal (reuses CreateProjectModal CSS).
function ConfirmModal({
  title,
  body,
  preview,
  confirmLabel,
  danger,
  busy,
  onConfirm,
  onClose,
}: {
  title: string;
  body: string;
  preview?: string;
  confirmLabel: string;
  danger?: boolean;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="modal-card" role="dialog" aria-modal="true" aria-label={title}>
        <button type="button" className="modal-close" onClick={onClose} disabled={busy} aria-label={t("common.close")}>✕</button>
        <h2 className="modal-card__title">{title}</h2>
        <div className="wipe-warning">{body}</div>
        {preview != null && <p className="assign-modal__preview">{preview}</p>}
        <div className="modal-actions">
          <button type="button" className="modal-btn modal-btn--ghost" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className={"modal-btn " + (danger ? "modal-btn--danger" : "modal-btn--accent")}
            disabled={busy}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
